/*
 * Bra bundle in the cart drawer.
 *
 * <bra-cart-bundle>  one card for every cart line carrying _bundle_tier.
 *                    Each bra's size and colour can be changed in place, the
 *                    stepper adds or removes a bra, and Remove drops the lot.
 * <bra-cart-upsell>  the strip along the bottom of that card: adds one more
 *                    bra, in the size and colour picked on it.
 *
 * Shopify cannot change a line's variant, so a size or colour change takes
 * one unit off the old line and adds it back as the new variant with the
 * same properties. Whenever the number of bras changes, every bundle line
 * needs the new count in _bundle_tier so the discount function sees one
 * consistent tier; rebuild() does that in two requests. The last request of
 * each action asks for the drawer sections and hands them to the drawer's
 * own renderContents().
 *
 * Every cart request here takes over a second, because the discount
 * function runs on each one, so the request count is what the shopper feels.
 */
(function () {
  'use strict';

  if (window.BraCart) return;

  var root = function () {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  };

  function post(path, body) {
    return fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json().then(function (json) {
        if (!r.ok || json.status) throw new Error(json.description || json.message || 'Cart update failed');
        return json;
      });
    });
  }

  function drawer() {
    return document.querySelector('cart-drawer');
  }

  function withSections(body) {
    var el = drawer();
    if (el && typeof el.getSectionsToRender === 'function') {
      body.sections = el.getSectionsToRender().map(function (s) {
        return s.id;
      });
      body.sections_url = window.location.pathname;
    }
    return body;
  }

  function render(state) {
    var el = drawer();
    if (el && state && state.sections && typeof el.renderContents === 'function') {
      try {
        el.renderContents(state);
        // renderContents replaces what is inside the drawer but not the
        // <cart-drawer> element's own is-empty class, which the empty cart
        // layout keys off; the theme's line updates set it themselves.
        // An add response has items but no item_count, and the cart then
        // holds bras; a plain section refresh says nothing either way.
        if ('item_count' in state) el.classList.toggle('is-empty', state.item_count === 0);
        else if (state.items) el.classList.remove('is-empty');
        return;
      } catch (e) {
        /* fall through to a reload */
      }
    }
    window.location.reload();
  }

  function bundleLines(cart) {
    return cart.items.filter(function (i) {
      return i.properties && i.properties._bundle_tier;
    });
  }

  function readCart() {
    return fetch(root() + 'cart.js').then(function (r) {
      return r.json();
    });
  }

  /* Take one bra off a bundle line, found by its key as rendered.

     Any change to the cart reissues every line key, and apps on the page
     can change the cart after the drawer has rendered, leaving the key on
     the row stale; Shopify then answers "no valid id or line parameter".
     In that case the cart is read afresh and the bundle line for the same
     variant is used instead. This costs a request only when it happens. */
  async function takeOneOff(key, variantId, quantity) {
    try {
      return await post('cart/change.js', { id: key, quantity: quantity - 1 });
    } catch (e) {
      var line = bundleLines(await readCart()).find(function (i) {
        return i.variant_id === variantId;
      });
      if (!line) throw e;
      return post('cart/change.js', { id: line.key, quantity: line.quantity - 1 });
    }
  }

  /* Clear every bundle line. The keys come from the drawer's render; if any
     were stale, whatever bundle lines survive are cleared by the fresh keys
     in the response, so the rebuild never leaves a doubled bundle. */
  async function clearBundle(keys) {
    var updates = {};
    keys.forEach(function (k) {
      updates[k] = 0;
    });
    var cart;
    try {
      cart = await post('cart/update.js', { updates: updates });
    } catch (e) {
      cart = await readCart();
    }
    var left = bundleLines(cart);
    if (!left.length) return;
    updates = {};
    left.forEach(function (i) {
      updates[i.key] = 0;
    });
    await post('cart/update.js', { updates: updates });
  }

  /* Re-render the drawer from the cart as it is, after a failed update has
     left the card showing a change that did not happen. */
  function refresh() {
    var el = drawer();
    if (!el || typeof el.getSectionsToRender !== 'function') return Promise.resolve();
    var ids = el.getSectionsToRender().map(function (s) {
      return s.id;
    });
    return fetch(window.location.pathname + '?sections=' + ids.join(','))
      .then(function (r) {
        return r.json();
      })
      .then(function (sections) {
        render({ sections: sections });
      })
      .catch(function () {});
  }

  /* Replace the whole bundle with the given bras, each tagged with the new
     count, in two requests however many lines there are. Retagging line by
     line cost one request per line, which made a 4-pack take eight seconds.

     Remove first, then add. Every change in this cart reissues every line
     key, so keys read before an add no longer name the right lines after
     it, and Shopify resolves an unknown key to another line of the same
     variant. The keys used here come from the drawer's last render, which
     is the cart as it stands. Shopify puts each added line on top, so the
     bras are added in reverse to keep their order.

     If the add fails, the old bundle is put back so it is never lost. */
  async function rebuild(oldKeys, oldVariants, newVariants) {
    function items(variants) {
      var tier = String(variants.length);
      return variants
        .slice()
        .reverse()
        .map(function (id) {
          return { id: id, quantity: 1, properties: { _bundle_tier: tier } };
        });
    }

    await clearBundle(oldKeys);
    try {
      return await post('cart/add.js', withSections({ items: items(newVariants) }));
    } catch (e) {
      var restored = await post('cart/add.js', withSections({ items: items(oldVariants) }));
      render(restored);
      throw e;
    }
  }

  function readVariants() {
    var node = document.querySelector('[data-bra-variants]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return [];
    }
  }

  function findVariant(size, colour) {
    return (
      readVariants().find(function (v) {
        return v.size === size && v.colour === colour;
      }) || null
    );
  }

  /* Shared busy/error handling for both elements. */
  var Busy = {
    busy: function (el, state) {
      el.classList.toggle('is-busy', state);
      // Checkout must wait for the cart to settle, or it would open with the
      // bundle half rebuilt.
      var checkout = document.getElementById('CartDrawer-Checkout');
      if (checkout) checkout.disabled = state;
      var footer = document.querySelector('cart-drawer .cart-drawer__footer');
      if (footer) footer.classList.toggle('is-updating', state);
      el.setAttribute('aria-busy', state ? 'true' : 'false');
      el.querySelectorAll('button, select').forEach(function (c) {
        c.disabled = state;
      });
    },
    error: function (el, message) {
      // A failed rebuild re-renders the drawer before reporting, so the
      // message goes to the card that replaced this one.
      if (!el.isConnected) el = document.querySelector('bra-cart-bundle') || el;
      var box = el.querySelector('[data-bundle-error]');
      if (!box) return;
      box.textContent = message || '';
      box.hidden = !message;
    },
    /* With optimistic set, the card already shows the change, so a failure
       re-renders the drawer from the real cart before reporting. */
    run: async function (el, task, optimistic) {
      Busy.error(el, '');
      Busy.busy(el, true);
      try {
        var state = await task();
        render(state);
      } catch (e) {
        console.error('bra-cart:', e);
        if (optimistic) await refresh();
        Busy.busy(el, false);
        Busy.error(el, e.message || 'Something went wrong. Please try again.');
      }
    },
  };

  window.BraCart = { rebuild: rebuild, findVariant: findVariant, post: post, withSections: withSections };

  customElements.define(
    'bra-cart-bundle',
    class BraCartBundle extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.count = parseInt(this.dataset.count, 10) || 0;

        // Keep changes to this card's selects away from the theme's
        // <cart-drawer-items>, which treats any change as a quantity edit and
        // posts /cart/change with no line ("no valid id or line parameter").
        this.addEventListener('change', (e) => {
          e.stopPropagation();
          var unit = e.target.closest('.bra-bundle__unit');
          if (unit) this.changeUnit(unit);
        });

        this.on('[data-bundle-minus]', () => this.removeOne());
        this.on('[data-bundle-plus]', () => this.addOne());
        this.on('[data-bundle-remove]', () => this.removeAll());

        // Typed quantity. Only acts on blur or Enter, so a half typed number
        // never triggers a cart write.
        var countEl = this.querySelector('[data-bundle-count]');
        if (countEl) {
          countEl.addEventListener('change', (e) => {
            e.stopPropagation();
            this.setCount(countEl);
          });
          countEl.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              countEl.blur();
            }
          });
        }
      }

      /* The bundle as rendered: line keys, and one variant id per bra in
         display order. */
      current() {
        var units = Array.prototype.slice.call(this.querySelectorAll('.bra-bundle__unit'));
        var keys = [];
        units.forEach(function (u) {
          if (keys.indexOf(u.dataset.key) === -1) keys.push(u.dataset.key);
        });
        return {
          units: units,
          keys: keys,
          variants: units.map(function (u) {
            return Number(u.dataset.variant);
          }),
        };
      }

      /* Show the new count on the card straight away, since the cart takes
         two seconds or so to answer. Prices are left as they are, marked as
         updating: they come from a store discount that is not linear, so
         they cannot be worked out here. */
      preview(n) {
        var value = this.querySelector('.bra-bundle__step-value');
        if (value && value.lastChild) value.lastChild.textContent = n;
        var tag = this.querySelector('.bra-bundle__tag');
        if (tag) tag.textContent = n === 1 ? 'Single' : n + '-pack';
      }

      on(selector, fn) {
        var el = this.querySelector(selector);
        if (el) el.addEventListener('click', fn);
      }

      changeUnit(unit) {
        var size = unit.querySelector('[data-unit-size]').value;
        var colour = unit.querySelector('[data-unit-colour]').value;
        var variant = findVariant(size, colour);

        if (!variant || !variant.available) {
          Busy.error(this, size + ' in ' + colour + ' is sold out. Please pick another.');
          return;
        }
        if (String(variant.id) === unit.dataset.variant) return;

        var key = unit.dataset.key;
        var tier = String(this.count);

        var qty = parseInt(unit.dataset.qty, 10) || 1;

        Busy.run(this, async () => {
          // A swap does not change how many bras are in the bundle, so the
          // tier is already right on every line and no rebuild is needed.
          // That takes this from five round trips to two.
          await takeOneOff(key, Number(unit.dataset.variant), qty);
          return post(
            'cart/add.js',
            withSections({
              items: [{ id: variant.id, quantity: 1, properties: { _bundle_tier: tier } }],
            })
          );
        });
      }

      /* Drop the last bra listed and re-tier what is left. */
      removeOne() {
        var now = this.current();
        if (!now.units.length) return;
        if (now.units.length === 1) return this.removeAll();

        this.preview(now.variants.length - 1);
        now.units[now.units.length - 1].hidden = true;
        Busy.run(this, () => rebuild(now.keys, now.variants, now.variants.slice(0, -1)), true);
      }

      /* Set the bundle to a typed quantity, growing or shrinking to match.
         Extra bras copy the last one's size and take unused colours. */
      setCount(input) {
        var now = this.current();
        var have = now.units.length;
        var want = parseInt(input.value, 10);

        if (isNaN(want) || want < 1) {
          input.value = have;
          return;
        }

        var max = parseInt(input.max, 10) || 20;
        if (want > max) want = max;
        input.value = want;

        if (want === have) return;
        if (want === 0) return this.removeAll();

        if (want < have) {
          this.preview(want);
          now.units.slice(want).forEach(function (u) {
            u.hidden = true;
          });
          Busy.run(this, () => rebuild(now.keys, now.variants, now.variants.slice(0, want)), true);
          return;
        }

        // Growing: keep the last bra's size and prefer colours not yet used,
        // falling back to repeats once all six are taken.
        var last = now.units[have - 1];
        var size = last.querySelector('[data-unit-size]').value;
        var used = now.units.map((u) => u.querySelector('[data-unit-colour]').value);
        var sameSize = readVariants().filter((v) => v.size === size && v.available);

        if (!sameSize.length) {
          input.value = have;
          Busy.error(this, 'Size ' + size + ' is sold out in every colour.');
          return;
        }

        var next = now.variants.slice();
        for (var i = have; i < want; i++) {
          var pick = sameSize.find((v) => used.indexOf(v.colour) === -1) || sameSize[0];
          used.push(pick.colour);
          next.push(pick.id);
        }

        this.preview(want);
        Busy.run(this, () => rebuild(now.keys, now.variants, next), true);
      }

      /* Same size as the last bra, first in-stock colour not yet chosen. */
      addOne() {
        var now = this.current();
        var last = now.units[now.units.length - 1];
        if (!last) return;

        var size = last.querySelector('[data-unit-size]').value;
        var used = now.units.map((u) => u.querySelector('[data-unit-colour]').value);
        var sameSize = readVariants().filter((v) => v.size === size && v.available);
        var pick = sameSize.find((v) => used.indexOf(v.colour) === -1) || sameSize[0];

        if (!pick) {
          Busy.error(this, 'Size ' + size + ' is sold out in every colour.');
          return;
        }

        this.addVariant(pick);
      }

      /* Add one bra, given as an entry from the variants JSON. */
      addVariant(variant) {
        var now = this.current();
        var last = now.units[now.units.length - 1];

        // Show the new row at once, as a copy of the last one set to the
        // new bra. It is replaced when the drawer re-renders.
        if (last) {
          var row = last.cloneNode(true);
          row.querySelector('[data-unit-size]').value = variant.size;
          row.querySelector('[data-unit-colour]').value = variant.colour;
          row.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
          var img = row.querySelector('.bra-bundle__thumb img');
          if (img && variant.image) img.src = variant.image;
          last.after(row);
        }

        this.preview(now.variants.length + 1);
        Busy.run(this, () => rebuild(now.keys, now.variants, now.variants.concat(variant.id)), true);
      }

      removeAll() {
        var updates = {};
        this.querySelectorAll('.bra-bundle__unit').forEach((u) => {
          updates[u.dataset.key] = 0;
        });

        Busy.run(this, () => post('cart/update.js', withSections({ updates: updates })));
      }
    }
  );

  customElements.define(
    'bra-cart-upsell',
    class BraCartUpsell extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.size = this.querySelector('[data-upsell-size]');
        this.colour = this.querySelector('[data-upsell-colour]');
        this.image = this.querySelector('[data-upsell-image]');
        // Busy state and errors belong to the whole card, not just the strip.
        this.card = this.closest('bra-cart-bundle') || this;

        var button = this.querySelector('[data-upsell-add]');
        if (button) button.addEventListener('click', () => this.add());

        [this.size, this.colour].forEach((s) => s && s.addEventListener('change', () => this.check()));
      }

      selected() {
        return findVariant(this.size.value, this.colour.value);
      }

      check() {
        var v = this.selected();
        if (v && v.image && this.image) this.image.src = v.image;
        Busy.error(this.card, v && v.available ? '' : this.size.value + ' in ' + this.colour.value + ' is sold out.');
      }

      add() {
        var variant = this.selected();
        if (!variant || !variant.available) return this.check();

        if (typeof this.card.addVariant === 'function') this.card.addVariant(variant);
      }
    }
  );
})();
